import { Link } from "react-router-dom";
import { RadialBar, RadialBarChart, PolarAngleAxis } from "recharts";
import { ROUTES } from "@/config/links";
import { ResultBox } from "@/components/diagnostics/ResultBox";

const CategoryBar = ({ category, naLabel }) => (
  <div data-testid={`readiness-category-${category.categoryId}`} className="py-2.5">
    <div className="flex items-center justify-between mb-1.5">
      <span className="font-body text-sm font-medium text-ink">{category.name}</span>
      <span className="font-body text-xs text-ink-soft">
        {category.applicable ? `${category.percentage}% · ${category.tierLabel}` : naLabel}
      </span>
    </div>
    <div className="h-2 rounded-full overflow-hidden bg-paper">
      {category.applicable && (
        <div className="h-full rounded-full bg-terracotta" style={{ width: `${category.percentage}%` }} />
      )}
    </div>
  </div>
);

/**
 * §8 — the immediate Results Page. NOT the PDF: no full methodology
 * here (that arrives by email, §9/§12). 2026-02: re-laid out as a
 * full-page box grid, matching the Executive Presence Communication
 * Diagnostic's own Results Page treatment for visual consistency
 * across both diagnostics — same content, same data-testids, same
 * donut + bar-chart components as before, just reorganized into
 * distinct boxes. Nothing added or removed.
 *
 * 2026-02 PT/EL localization — all copy comes from `misc`
 * (`definition.miscCopy`, locale-resolved by the backend), mirroring
 * the Executive Presence Communication Diagnostic's own `ResultsCard`.
 */
export const ResultsCard = ({ result, email, misc = {} }) => {
  const strongest = result.categories.find((c) => c.categoryId === result.strongestCategoryId);
  const firstPriority = result.categories.find((c) => c.categoryId === result.firstPriorityCategoryId);
  const naLabel = misc.results_not_applicable || "Not applicable";
  const [emailNoticePrefix, emailNoticeSuffix] = (misc.results_email_notice_body || "{email}").split("{email}");

  return (
    <div data-testid="readiness-results-card" className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-16 space-y-6">
      {/* Overall — hero box */}
      <ResultBox tone="dark" testId="readiness-overall-section" className="flex flex-col md:flex-row items-center gap-6">
        <div className="relative w-36 h-36 shrink-0" data-testid="readiness-overall-chart">
          <RadialBarChart
            width={144} height={144} cx={72} cy={72} innerRadius={56} outerRadius={70} barSize={14}
            data={[{ value: result.overall.percentage, fill: "#DA983C" }]}
            startAngle={90} endAngle={-270}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
            <RadialBar dataKey="value" background={{ fill: "rgba(242,235,218,0.18)" }} cornerRadius={8} />
          </RadialBarChart>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-serif text-3xl text-paper">{result.overall.percentage}%</span>
          </div>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-[0.2em] mb-1 text-paper/70">{misc.results_overall_label}</p>
          <p className="font-serif text-2xl text-paper" data-testid="readiness-overall-tier">{result.overall.tierLabel}</p>
        </div>
      </ResultBox>

      {/* Category Breakdown */}
      <ResultBox tone="deep" testId="readiness-category-breakdown">
        <p className="font-serif text-xl text-charcoal mb-3">{misc.results_category_breakdown_title}</p>
        <div className="divide-y divide-ink/10">
          {result.categories.map((c) => (
            <CategoryBar key={c.categoryId} category={c} naLabel={naLabel} />
          ))}
        </div>
      </ResultBox>

      {/* Strongest Area + Where to Start */}
      <div className="grid md:grid-cols-2 gap-6">
        {strongest && (
          <ResultBox tone="deep" testId="readiness-strongest-area">
            <p className="font-body text-xs uppercase tracking-[0.2em] mb-2 text-terracotta">
              {result.strongestTied ? result.strongestTieLabel : misc.results_strongest_area_title}
            </p>
            <p className="font-serif text-xl text-charcoal mb-2">{strongest.name}</p>
            <p className="font-body text-sm leading-relaxed text-ink-soft">{strongest.resultCopy}</p>
          </ResultBox>
        )}
        {firstPriority && (
          <ResultBox testId="readiness-first-priority">
            <p className="font-body text-xs uppercase tracking-[0.2em] mb-2 text-terracotta">{misc.results_priority_title}</p>
            <p className="font-serif text-xl text-charcoal mb-2">{firstPriority.name}</p>
            <p className="font-body text-sm leading-relaxed text-ink-soft">{firstPriority.resultCopy}</p>
            {result.dependencyBridgeCopy && (
              <p className="font-body text-sm leading-relaxed text-ink-soft italic mt-3" data-testid="readiness-dependency-bridge">
                {result.dependencyBridgeCopy}
              </p>
            )}
          </ResultBox>
        )}
      </div>

      {/* Email notice */}
      <ResultBox tone="deep" testId="readiness-email-notice">
        <p className="font-body text-sm leading-relaxed text-ink">
          {emailNoticePrefix}
          <strong data-ph-mask>{email}</strong>
          {emailNoticeSuffix}
        </p>
      </ResultBox>

      {/* About your coach + CTA */}
      <ResultBox testId="readiness-coach-bio">
        <p className="font-serif text-lg text-charcoal mb-2">{misc.coach_bio_heading}</p>
        <p className="font-body text-sm leading-relaxed text-ink-soft mb-2">{misc.coach_bio_results_page_p1}</p>
        <p className="font-body text-sm leading-relaxed text-ink-soft mb-2">{misc.coach_bio_results_page_p2}</p>
        <p className="font-body text-sm leading-relaxed text-ink-soft mb-6">{misc.coach_bio_results_page_p3}</p>
        <Link to={ROUTES.bookPresentation} data-testid="readiness-book-consultation-cta">
          <button
            type="button"
            className="w-full py-4 rounded-full font-body text-sm font-medium bg-terracotta text-paper hover:bg-terracotta-dark transition-colors"
          >
            {misc.book_consultation_button}
          </button>
        </Link>
      </ResultBox>
    </div>
  );
};
